import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from "react-router-dom";
import { stytchService } from '../utils/stytchService';
import type { RootState } from "../store/store.ts";
import { useNavUtils, removeAuthQueryParamsFromUrl } from "../utils/navUtils";
import { encryptAndStoreToken, hideEncryptedToken } from "../utils/tokenEncryptionUtils";
import { Size } from "../utils/types";
import HeroCardLayout from "./layout/HeroCardLayout";
import UserLoginContainer from "./entryflow/UserLoginContainer";
import OTPVerificationContainer from "./entryflow/OTPVerificationContainer";
import LoadingUserComponent from "./entryflow/LoadingUser";
import SelectorContainer from "./entryflow/SelectorContainer";
import PatientIntakeView from "./entryflow/PatientIntakeView";
import { 
  selectSelectorId, 
  setSelectorId,
  setResLinkParamsId,
  setOrganizationId,
  selectResLinkParamsId, 
  selectOrganizationId,
} from '../store/experienceSlice';
import {
  setUser,
  pushUser,
  setTempProfilePictureURL,
  setActivePixelStreamingExperience,
} from "../store/userSlice";
import { 
  setSubClientMode, 
  selectSubClientMode, 
  setSubClientModeAuthorized, 
  selectSubClientModeAuthorized,
} from '../store/authPreferencesSlice';
import { APIUser } from "../utils/userEndpoints";
import { getPatientJWT } from "../utils/partnerEndpoints";
import { getSelector } from "../utils/miscEndpoints";
import { performDirectTokenCookieExchange } from "../utils/partnerEndpoints";
import { getOrCreateUser, getPartnerUserAndExperience } from "./entryflow/userFlowUtils";
import { getSavedContentSize } from "../hooks/useContentSize";
import "../styles/components/LoginUniversal.css";
import UserTerminalComponent from "./entryflow/UserTerminal";
import { getLanguage, setLanguage } from "../utils/languageUtils";
import i18n from "../i18n/config";

enum LoginViewState {
  BLANK = "blank",
  INITIAL = "initial",
  SELECTOR = "selector",
  VERIFICATION = "verification",
  LOADING = "loading",
  SUBCLIENT_INTAKE = "subclient_intake",
  SUBCLIENT_ERROR = "subclient_error",
}

const SUBCLIENT_MODE_NOT_AUTHORIZED_ERROR = "You are not authorized for sub-client access.";
const SUBCLIENT_REQUIRED_SCOPE = "loginexternaluser:view";

const LoginUniversal: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const { getSessionCreationTarget, goNav } = useNavUtils();

  const [currentView, setCurrentView] = useState<LoginViewState>(LoginViewState.BLANK);
  const [email, setEmail] = useState("");
  const [stytchMethodId, setStytchMethodId] = useState("");
  const [contentSize, setContentSize] = useState<Size | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [localSelectorId, setLocalSelectorId] = useState<string | undefined>(undefined);

  const [patientNumber, setPatientNumber] = useState<string | null>(null);
  const [patientFirstName, setPatientFirstName] = useState<string | null>(null);
  const [patientLastName, setPatientLastName] = useState<string | null>(null);

  const handleBackToLogin = () => {
    setStytchMethodId("");
    setCurrentView(LoginViewState.INITIAL);
  };

  const handleEmailLoginSuccess = (methodId: string, email: string) => {
    setStytchMethodId(methodId);
    setEmail(email);
    setCurrentView(LoginViewState.VERIFICATION);
  };

  const getSelectorId = () => {
    const reduxState = reduxStore.getState() as RootState;
    return selectSelectorId(reduxState);
  }

  const hasSelector = () => {
    return !!getSelectorId();
  }

  const isSubClientMode = () => {
    const reduxState = reduxStore.getState() as RootState;
    return selectSubClientMode(reduxState);
  }

  const currentReduxOrganizationId = () => {
    const reduxState = reduxStore.getState() as RootState;
    return selectOrganizationId(reduxState);
  }

  const currentReduxResLinkParamsId = () => {
    const reduxState = reduxStore.getState() as RootState;
    return selectResLinkParamsId(reduxState);
  }

  const isSubClientModeAuthorized = () => {
    const reduxState = reduxStore.getState() as RootState;
    return selectSubClientModeAuthorized(reduxState);
  }

  const getOrgFromSelectorId = async(selectorId: string | null): Promise<string | null> => {
    console.log("[LoginUniversal] getOrgFromSelectorId for selectorId:", selectorId);
    if (!selectorId) return null;
    const selector = await getSelector(selectorId);
    return selector?.organization_id || null;
  }

  const getActiveOrganizationId = async(selectorIdParam?: string): Promise<string | null> => {
    // We need an organization id to get proper scopes for the user.
    // Try to get the org id in this order:
    // 1. From redux state
    // 2. From selector org id from selector id in redux
    // 3. From selector org id from selector id in params
    console.log("[LoginUniversal] getActiveOrganizationId with param selectorId:", selectorIdParam);
    let orgId = currentReduxOrganizationId() ||
      await getOrgFromSelectorId(getSelectorId() || null) ||
      await getOrgFromSelectorId(selectorIdParam || null);
    return orgId || null;
  };

  const getAndStorePersonaUser = async (userId: string | undefined, orgId: string | null, userEmail?: string): Promise<APIUser | null> => {
    try {
      const user = await getOrCreateUser(userId, userEmail, undefined, orgId || undefined);
      console.log("[LoginUniversal] getAndStorePersonaUser got user:", user);

      // TODO yes handle, but also have to push and pop
      //console.log("[SessionCreationLobby] saving profilePicture:", profilePicture);
      //dispatch(setTempProfilePictureURL(profilePicture));
      dispatch(setUser(user));
      return user;
    } catch (err) {
      console.error("[LoginUniversal] getAndStorePersonaUser error:", err);
    }
    return null;
  };

  const authenticationFinished = async () => {
    if (isSubClientMode()) {
      // If we're in sub-client mode, then restore the url, which will 
      // trigger then main effect again, and it'll handle the subsequent flow.
      restoreUrlIfNoQuery(true);
      return;
    }

    const su = stytchService.getUserSync();

    console.log("[LoginUniversal] authenticationFinished, has stytch user:", su);
    if (!su) return;

    const userId = (su.trusted_metadata as { user_id?: string })?.user_id;
    const userEmail = su.emails?.[0]?.email;

    const orgId = await getActiveOrganizationId();
    console.log("[LoginUniversal] authenticationFinished, active organizationId:", orgId);

    const user = await getAndStorePersonaUser(userId, orgId, userEmail);
    if (!user) {
      console.error("[LoginUniversal] authenticationFinished, missing user; cannot proceed.");
      return;
    }
 
    goNav(getSessionCreationTarget());
  };

  const subclientEntry = async (organizationId: string, resLinkParamsId: string) => {
    if (!patientNumber || !patientFirstName) {
      if (isSubClientModeAuthorized()) {
        setError(undefined);
        setCurrentView(LoginViewState.SUBCLIENT_INTAKE);
      } else {
        setError(SUBCLIENT_MODE_NOT_AUTHORIZED_ERROR);
        setCurrentView(LoginViewState.SUBCLIENT_ERROR);
      }
      return;
    }
    setCurrentView(LoginViewState.LOADING);
    try {
      console.log("[LoginUniversal] Getting patient JWT");
      const jwt = await getPatientJWT(organizationId, patientNumber, patientFirstName, undefined, patientLastName || undefined);
      hideEncryptedToken();
      await performDirectTokenCookieExchange(resLinkParamsId, organizationId, jwt);
      const { user, experience } = await getPartnerUserAndExperience(resLinkParamsId, organizationId);
      console.log("[LoginUniversal] subclientEntry got user and experience:", user, experience);
      dispatch(pushUser(user));
      dispatch(setActivePixelStreamingExperience(experience));
      if (experience.organization_id) {
        dispatch(setOrganizationId(experience.organization_id))
      }
      goNav("/subcliententry", {replace: true});
    } catch (err) {
      console.error("[LoginUniversal] Error getting patient JWT:", err);
      setError("Error during patient intake. Please try again.");
      removeAuthQueryParamsFromUrl();
      setCurrentView(LoginViewState.SUBCLIENT_INTAKE);
    }
  };

  const intakeSubClient = async (patientNumber: string, patientFirstName: string, patientLastName: string) => {
    console.log("[LoginUniversal] intakeSubClient:", patientNumber, patientFirstName, patientLastName);
    setContentSize(getSavedContentSize());
    setLocalSelectorId(getSelectorId());
    setPatientNumber(patientNumber);
    setPatientFirstName(patientFirstName);
    setPatientLastName(patientLastName);
    if (hasSelector()) {
      setCurrentView(LoginViewState.SELECTOR);
    } else {
      const reduxOrganizationId = currentReduxOrganizationId();
      const reduxResLinkParamsId = currentReduxResLinkParamsId();
      if (reduxOrganizationId && reduxResLinkParamsId) {
        await subclientEntry(reduxOrganizationId, reduxResLinkParamsId);
      } else {
        // Not a user error - shit's misconfigured
        console.error("[LoginUniversal] Missing organizationId or resLinkParamsId in redux state after patient intake.");
        setError("Error during patient intake. Please try again.");
        setCurrentView(LoginViewState.SUBCLIENT_INTAKE);
      }
    }
  }

  const handleSelectorContinue = async (organizationId: string, resLinkParamsId: string) => {
    console.log("[LoginUniversal] handleSelectorContinue org:", organizationId, "res:", resLinkParamsId);
    setContentSize(getSavedContentSize());
    dispatch(setOrganizationId(organizationId));
    dispatch(setResLinkParamsId(resLinkParamsId));

    if (isSubClientMode()) {
      await subclientEntry(organizationId, resLinkParamsId);
    } else {
      setCurrentView(LoginViewState.INITIAL);
    }
  };

  // The deal with url replacement stuff: whatever the url
  // is, we replace it with the 'previous' url in session storage.
  // We do this after oauth, and in order to support the correct
  // parameters from restore after logout. 
  const saveUrl = useCallback(() => {
    const { pathname, search } = location;
    sessionStorage.setItem("pre_oauth_path", pathname);
    sessionStorage.setItem("pre_oauth_query", search);
  }, [location]);

  const restoreUrlIfNoQuery = useCallback((force: boolean = false) => {
    const { search } = location;
    if (force || !search || search === "") {
      const savedPath = sessionStorage.getItem("pre_oauth_path");
      const savedQuery = sessionStorage.getItem("pre_oauth_query");

      if (savedQuery && savedPath) {
        console.log("[LoginUniversal] Restoring pre-oauth URL:", savedPath, savedQuery);

        // Replace the URL silently (no extra history entry)
        goNav(savedPath, { 
          includeParams: false,
          extraParams: savedQuery,
          replace: true,
        });
      } 
    }
  }, [location]);

  const authenticateOAuthToken = async (token: string, stytchTokenType: string) => {
    console.log("[LoginUniversal] Post oauth-authenticating ...");

    // Read the previous content size from sessionStorage;  we will use
    // this to display the loading card the same size as the login card.
    setContentSize(getSavedContentSize());
    setCurrentView(LoginViewState.LOADING);

    try {
      if (stytchTokenType !== "oauth") {
        throw new Error("Unsupported authentication method");
      }

      const response = await stytchService.authenticateOAuth(token);

      if (response && response.session_jwt) {
        encryptAndStoreToken(response.session_jwt);
      } else {
        console.error("[LoginUniversal] No session_jwt in Stytch response");
      }

      if (response && response.session_token) {
        localStorage.setItem("stytch_session", response.session_token);
      }

      authenticationFinished();
    } catch (err) {
      console.error("[LoginUniversal] Authentication error:", err);
      setError("Authentication failed. Please try again.");
      setCurrentView(LoginViewState.INITIAL);
    }
  };

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");
      const stytchTokenType = params.get("stytch_token_type");
      const paramSelectorId = params.get('selector');
      const paramResLinkParamsId = params.get('res_link_params_id');
      const paramOrganizationId = params.get('org_id');
      const isSubClientMode = params.get('subClientMode') === 'true';

      console.log("[LoginUniversal] start at", location.pathname, location.search);

      setError(undefined);

      // We're in oauth callback mode, pre-empt other state writing.
      if (token && stytchTokenType) {
        console.log("[LoginUniversal] start: oauth mode");
        setCurrentView(LoginViewState.LOADING);
        authenticateOAuthToken(token, stytchTokenType);
        return;
      }
      
      dispatch(setSubClientMode(isSubClientMode));
      restoreUrlIfNoQuery();
      saveUrl();
      setLocalSelectorId(undefined);

      // read the language
      const languageOverride = getLanguage(false);
      setLanguage(languageOverride);
      i18n.changeLanguage(languageOverride);

      const stytchUser = await stytchService.getUserOnce();
      console.log("[LoginUniversal] start: stytch user:", stytchUser);

      // We're logged in, but in sub client mode, so go to intake.
      if (isSubClientMode && stytchUser) {
        console.log("[LoginUniversal] start: sub-client intake with selector=", paramSelectorId, "reslink=", paramResLinkParamsId, "org=", paramOrganizationId);

        const fail = (msg: string) => {
          console.log(`[LoginUniversal] ${msg}`);
          dispatch(setSubClientModeAuthorized(false));
          setError(SUBCLIENT_MODE_NOT_AUTHORIZED_ERROR);
          setCurrentView(LoginViewState.SUBCLIENT_ERROR);
        };

        const userId = (stytchUser.trusted_metadata as { user_id?: string })?.user_id;
        const userEmail = stytchUser.emails?.[0]?.email;

        if (!userId) {
          fail("[LoginUniversal] No user ID found in trusted metadata; cannot proceed in sub-client mode.");
          return;
        }

        const orgId = await getActiveOrganizationId(paramSelectorId || undefined) || paramOrganizationId;
        if (!orgId) {
          fail("[LoginUniversal] No organization ID found; cannot proceed in sub-client mode.");
          return;
        }

        const user = await getAndStorePersonaUser(userId, orgId, userEmail);
        if (!user) {
          fail("[LoginUniversal] No user found; cannot proceed in sub-client mode.");
          return;
        }

        if (!user.user_scopes || !user.user_scopes.includes(SUBCLIENT_REQUIRED_SCOPE)) {
          fail("[LoginUniversal] User NOT authorized for sub-client hosting.");
          return;
        }

        console.log("[LoginUniversal] user authorized for sub-client hosting.");
        dispatch(setSubClientModeAuthorized(true));
        dispatch(setSelectorId(paramSelectorId || undefined));
        dispatch(setResLinkParamsId(paramResLinkParamsId || undefined));
        dispatch(setOrganizationId(paramOrganizationId || undefined));
        setLocalSelectorId(paramSelectorId || undefined);
        setError(undefined);
        setCurrentView(LoginViewState.SUBCLIENT_INTAKE);
        return;
      }

      // We are in 'sub-client' mode - a user can log in, and then 'sub-clients'
      // can also log in without authentication (i.e. Doctor first, then patients).
      if (isSubClientMode) {
        console.log("[LoginUniversal] start: sub-client mode");
        dispatch(setSelectorId(paramSelectorId || undefined));
        dispatch(setResLinkParamsId(paramResLinkParamsId || undefined));
        dispatch(setOrganizationId(paramOrganizationId || undefined));
        setLocalSelectorId(paramSelectorId || undefined);
        setCurrentView(LoginViewState.INITIAL);
        return;
      }

      // We have a 'selector' query parameter.
      if (paramSelectorId) {
        console.log("[LoginUniversal] start: param selector mode");
        dispatch(setSelectorId(paramSelectorId));
        dispatch(setResLinkParamsId(undefined));
        dispatch(setOrganizationId(undefined));
        setLocalSelectorId(paramSelectorId);
        setCurrentView(LoginViewState.SELECTOR);
        return;
      }

      // We have a 'res_link_params_id' query parameter.
      if (paramResLinkParamsId && paramOrganizationId) {
        console.log("[LoginUniversal] start: param reslink mode");
        dispatch(setSelectorId(undefined));
        dispatch(setResLinkParamsId(paramResLinkParamsId));
        dispatch(setOrganizationId(paramOrganizationId));
        setLocalSelectorId(undefined);
        setCurrentView(LoginViewState.INITIAL);
        return;
      }
    })();

  }, [dispatch, location.key, location.search]);

  const renderCurrentView = () => {
    switch (currentView) {
      case LoginViewState.BLANK:
        return <HeroCardLayout showCard={false}>{null}</HeroCardLayout>;
      case LoginViewState.SELECTOR:
        return (
          <HeroCardLayout>
            <SelectorContainer
              selectorId={localSelectorId!}
              onContinue={handleSelectorContinue}
            />
          </HeroCardLayout>
        );
      case LoginViewState.LOADING:
        return (
          <HeroCardLayout>
            <div
              style={{
                minHeight: contentSize?.height || "auto",
                minWidth: contentSize?.width || "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <LoadingUserComponent />
            </div>
          </HeroCardLayout>
        );
      case LoginViewState.VERIFICATION:
        return (
          <HeroCardLayout>
            <OTPVerificationContainer
              email={email}
              stytchMethodId={stytchMethodId}
              onCodeAuthenticated={authenticationFinished}
              onBackToLogin={handleBackToLogin}
            />
          </HeroCardLayout>
        );
      case LoginViewState.SUBCLIENT_INTAKE:
        return (
          <HeroCardLayout errorMessage={error}>
            <PatientIntakeView
              onContinue={intakeSubClient}
            />
          </HeroCardLayout>
        );
      case LoginViewState.SUBCLIENT_ERROR:
        return (
          <HeroCardLayout>
            <UserTerminalComponent reason={error}/>
          </HeroCardLayout>
        );
      default:
        return (
          <HeroCardLayout errorMessage={error}>
            <UserLoginContainer onEmailLoginSuccess={handleEmailLoginSuccess} />
          </HeroCardLayout>
        );
    }
  };

  return renderCurrentView();
};

export default LoginUniversal;
